import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Alert,
  Box,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from "@mui/material";
import * as React from "react";

import {
  RobotoLinkHoverUnderline,
  RobotoTableCell,
  RobotoTableRow,
} from "@/shared/components";
import type { DateRange } from "@/shared/components";
import { ActionRecord, InvocationRecord } from "@/shared/domain/actions";
import {
  invocationActionCondition,
  invocationDatasetCondition,
  invocationInitiatorCondition,
  invocationStatusCondition,
  invocationDateRangeCondition,
  invocationSourceCondition,
} from "@/shared/domain/actions/invocationSearchConditions";
import {
  OrganizationTier,
  OrganizationType,
  OrgRecord,
} from "@/shared/domain/orgs";
import { OrganizationStatus } from "@/shared/domain/orgs/OrgRecord";
import { usePaginatedAPICall } from "@/shared/services/apiHooks";
import {
  Condition,
  RobotoAPICall,
  SearchQueryBody,
  searchInvocationsEndpoint,
} from "@/types";

import InvocationRow from "./invocation/InvocationRow";
import {
  type UserFacingStatus,
  userFacingStatusToInternalStatuses,
} from "./invocation/invocationStatusUtils";
import { InvocationControlBar } from "./InvocationControlBar";

type RowsPerPage = 10 | 25 | 50;
const initialRowsPerPage = 25;

// Column sort type following Tanstack Table pattern
type ColumnSort = {
  id: string; // Column name
  desc: boolean; // Whether it's descending (true) or ascending (false)
};

// Sortable columns for invocations
type SortableColumn = "created" | "duration";

// Map UI column names to backend field names
const COLUMN_TO_FIELD_MAP: Record<SortableColumn, string> = {
  created: "created",
  duration: "duration",
};

interface InvocationsProps {
  currentOrg: OrgRecord | null;
  datasetId?: string;
  action?: ActionRecord;
  triggerId?: string;
  condition?: Condition;
}

const kindaSketchyCurrentOrgFromJustId = (orgId: string): OrgRecord => {
  return {
    org_id: orgId,
    org_type: OrganizationType.Team,
    created: "2024-01-01T00:00Z",
    name: "FAKE_ORG",
    tier: OrganizationTier.Free,
    status: OrganizationStatus.Active,
  };
};

// Sortable table cell component
const SortableTableCell: React.FC<{
  children: React.ReactNode;
  sortKey?: SortableColumn;
  columnSort: ColumnSort;
  handleSort: (field: SortableColumn) => void;
}> = ({ children, sortKey, columnSort, handleSort }) => {
  const theme = useTheme();

  if (!sortKey) {
    return <RobotoTableCell>{children}</RobotoTableCell>;
  }

  const isActive = columnSort.id === sortKey;
  const isDesc = isActive && columnSort.desc;

  return (
    <RobotoTableCell
      sx={{
        cursor: "pointer",
        userSelect: "none",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      }}
      onClick={() => handleSort(sortKey)}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {children}
        <IconButton
          size="small"
          sx={{
            marginLeft: theme.spacing(0.5),
            transition: "all 0.3s ease-in-out",
            transform: isDesc ? "rotate(180deg)" : "rotate(0deg)",
            opacity: isActive ? 1 : 0.3,
          }}
        >
          <ArrowUpwardIcon
            fontSize="small"
            color={isActive ? "primary" : "disabled"}
          />
        </IconButton>
      </Box>
    </RobotoTableCell>
  );
};

export const Invocations: React.FC<InvocationsProps> = ({
  currentOrg,
  datasetId,
  action,
  triggerId,
  condition,
}) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] =
    React.useState<number>(initialRowsPerPage);
  const [statusFilters, setStatusFilters] = React.useState<UserFacingStatus[]>(
    [],
  );

  // Source filter state - default to show all (empty array means show all)
  const [sourceFilters, setSourceFilters] = React.useState<string[]>([]);

  // Date range state - default to All Time (no time constraints)
  const [dateRange, setDateRange] = React.useState<DateRange>({
    startDate: null,
    endDate: null,
  });

  // Sorting state - default to created date descending
  const [columnSort, setColumnSort] = React.useState<ColumnSort>({
    id: "created",
    desc: true,
  });

  const {
    getFirstPage,
    fetchNextPage,
    fetchPreviousPage,
    onRowsPerPageChange,
    pageData,
    isNextPageAvailable,
    cacheLength,
    updateItemsInCache,
    error,
  } = usePaginatedAPICall<InvocationRecord>();

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const apiCall = {} as RobotoAPICall;

      apiCall.endpoint = searchInvocationsEndpoint;
      apiCall.method = "POST";
      apiCall.orgId = currentOrg?.org_id;

      const requestBody = {} as SearchQueryBody;
      // Apply current sorting
      requestBody.sort_direction = columnSort.desc ? "DESC" : "ASC";
      requestBody.sort_by =
        COLUMN_TO_FIELD_MAP[columnSort.id as SortableColumn] || "created";
      requestBody.limit = 100;

      // Build base condition
      let baseCondition: Condition | undefined;
      if (condition) {
        baseCondition = condition;
      } else if (datasetId) {
        baseCondition = invocationDatasetCondition(datasetId);
      } else if (action) {
        baseCondition = invocationActionCondition(
          action?.org_id ?? currentOrg?.org_id,
          action.name,
        );
      } else if (triggerId) {
        baseCondition = invocationInitiatorCondition(triggerId, "Trigger");
      }

      // Build array of conditions to combine
      const conditions: Condition[] = [];

      // Add base condition if it exists
      if (baseCondition) {
        conditions.push(baseCondition);
      }

      // Add status filter condition if statuses are selected
      if (statusFilters.length > 0) {
        const internalStatuses = statusFilters.flatMap(
          userFacingStatusToInternalStatuses,
        );
        const statusCondition = invocationStatusCondition(internalStatuses);
        conditions.push(statusCondition);
      }

      // Add date range condition if dates are valid
      if (dateRange.startDate && dateRange.endDate) {
        const startTime = dateRange.startDate.utc().toISOString();
        const endTime = dateRange.endDate.utc().toISOString();
        const dateCondition = invocationDateRangeCondition(startTime, endTime);
        conditions.push(dateCondition);
      }

      // Add source filter conditions if any sources are selected
      if (sourceFilters.length > 0) {
        const sourceConditions = sourceFilters.map((source) =>
          invocationSourceCondition(source === "User" ? "Manual" : "Trigger"),
        );

        if (sourceConditions.length === 1) {
          conditions.push(sourceConditions[0]);
        } else {
          conditions.push({
            conditions: sourceConditions,
            operator: "OR",
          });
        }
      }

      // Combine all conditions
      if (conditions.length === 1) {
        requestBody.condition = conditions[0];
      } else if (conditions.length > 1) {
        requestBody.condition = {
          conditions,
          operator: "AND",
        };
      }

      apiCall.requestBody = JSON.stringify(requestBody);

      await getFirstPage(apiCall, rowsPerPage);

      setIsLoading(false);
    };

    void fetchData();
  }, [
    getFirstPage,
    currentOrg?.org_id,
    datasetId,
    action,
    triggerId,
    condition,
    statusFilters,
    sourceFilters,
    dateRange.startDate,
    dateRange.endDate,
    columnSort.id,
    columnSort.desc,
    rowsPerPage,
  ]);

  const theme = useTheme();

  // Helper function to handle column sorting
  const handleSort = (field: SortableColumn) => {
    if (columnSort.id === field) {
      setColumnSort({ id: field, desc: !columnSort.desc });
    } else {
      setColumnSort({
        id: field,
        desc: true, // Default to descending for all sortable columns
      });
    }
    // Reset to first page when sorting changes
    setPage(0);
  };

  const onUpdate = React.useCallback(
    (record: InvocationRecord) => {
      updateItemsInCache({ [record.invocation_id]: record }, "invocation_id");
    },
    [updateItemsInCache],
  );

  const onPageChangeHandler = async (newPage: number) => {
    //
    if (newPage > page) {
      setPage(page + 1);
      await fetchNextPage(newPage, rowsPerPage, "body");
    } else {
      setPage(page - 1);
      fetchPreviousPage(newPage, rowsPerPage);
    }
  };

  const onRowsPerPageChangeHandler = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = parseInt(e?.target?.value) ?? 10;
    setPage(0);
    setRowsPerPage(val);
    await onRowsPerPageChange(val as RowsPerPage);
  };

  return (
    <div>
      {!datasetId && !condition && (
        <Alert severity="info" style={{ marginBottom: "var(--spacing-md)" }}>
          Invocations are runs of an action on files in a dataset.{" "}
          {currentOrg?.tier === OrganizationTier.Free && (
            <>
              In Roboto&apos;s free tier, invocations are run serially. Upgrade
              to{" "}
              <RobotoLinkHoverUnderline
                to={"https://www.roboto.ai/pricing"}
                target="_blank"
              >
                premium
              </RobotoLinkHoverUnderline>{" "}
              to run them faster in parallel.
            </>
          )}
        </Alert>
      )}
      {error && (
        <Alert severity="error" style={{ marginBottom: "var(--spacing-md)" }}>
          Failed to load invocations. Refresh the page to try again. Please
          contact <a href="mailto:support@roboto.ai">support@roboto.ai</a> if
          this problem persists.
        </Alert>
      )}
      <InvocationControlBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        defaultQuickFilter={{ amount: null, unit: "day", label: "All Time" }}
        selectedStatuses={statusFilters}
        onStatusChange={setStatusFilters}
        selectedSources={sourceFilters}
        onSourceChange={setSourceFilters}
        action={action}
        datasetId={datasetId}
      />
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>Invocation</RobotoTableCell>
              <RobotoTableCell>Status</RobotoTableCell>
              <RobotoTableCell>Action</RobotoTableCell>
              {!datasetId && <RobotoTableCell>Dataset</RobotoTableCell>}
              <SortableTableCell
                sortKey="duration"
                columnSort={columnSort}
                handleSort={handleSort}
              >
                Duration
              </SortableTableCell>
              <SortableTableCell
                sortKey="created"
                columnSort={columnSort}
                handleSort={handleSort}
              >
                Date
              </SortableTableCell>
              <RobotoTableCell>Run By</RobotoTableCell>
              <RobotoTableCell>Logs</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: 3 }, (_, i) => (
                  <TableRow key={`row-${i}`}>
                    {Array.from({ length: datasetId ? 7 : 8 }, (_, j) => (
                      <RobotoTableCell key={`cell-${j}`}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          height={30}
                          sx={{ mt: 1, mb: 1 }}
                        />
                      </RobotoTableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            ) : (
              <>
                {pageData?.length > 0 ? (
                  <>
                    {pageData.map((invocation) => {
                      return (
                        <InvocationRow
                          key={invocation.invocation_id}
                          invocation={invocation}
                          currentOrg={
                            currentOrg ||
                            kindaSketchyCurrentOrgFromJustId(invocation.org_id)
                          }
                          onUpdate={onUpdate}
                          showDatasetId={datasetId ? false : true}
                        />
                      );
                    })}
                  </>
                ) : (
                  <RobotoTableRow>
                    <RobotoTableCell
                      style={{
                        paddingBottom: theme.spacing(2),
                        paddingTop: theme.spacing(2),
                      }}
                      colSpan={datasetId ? 7 : 8}
                    >
                      <Alert severity="warning">
                        No actions have been invoked in this time range. You can
                        invoke one using the button above, or with the{" "}
                        <Link
                          underline="hover"
                          target="_blank"
                          href="https://docs.roboto.ai/getting-started/programmatic-access.html#python-sdk-and-cli"
                        >
                          Roboto CLI
                        </Link>
                        :
                        <br />
                        <br />
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                          }}
                        >
                          $ roboto actions invoke {"<actionReference>"}{" "}
                          --dataset-id {datasetId ? datasetId : "<datasetId>"}{" "}
                          --input-data {"<filePattern>"}
                        </span>
                      </Alert>
                    </RobotoTableCell>
                  </RobotoTableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={isNextPageAvailable ? -1 : cacheLength}
        nextIconButtonProps={{
          disabled: !isNextPageAvailable,
        }}
        rowsPerPageOptions={[10, 25, 50]}
        page={page}
        onPageChange={(_, newPage) => {
          void onPageChangeHandler(newPage);
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          void onRowsPerPageChangeHandler(e);
        }}
        labelRowsPerPage={"Invocations per page:"}
      />
    </div>
  );
};
